import {
  Baby, Banknote, Bike, Bitcoin, BookOpen, Briefcase, Building2, Car, Coffee, CreditCard, Dog, Dumbbell, Film, Fuel, Gift,
  GraduationCap, HandCoins, Heart, Home, Landmark, LineChart, Music, Palmtree, PiggyBank, Plane, Receipt, Shield, Shirt, ShoppingBag,
  ShoppingCart, Smartphone, Sparkles, Stethoscope, Target, TrainFront, TrendingUp, Umbrella, Utensils, Wallet, Wrench, Zap,
  type LucideIcon,
} from 'lucide-react';
import { useMemo } from 'react';
import { useChartTheme } from '@/components/ui';
import { cn } from '@/lib/cn';

/** Curated icon set for accounts, categories and goals. Stored by name on the record (`icon: 'PiggyBank'`). */
export const MONEY_ICONS: Readonly<Record<string, LucideIcon>> = {
  Wallet, PiggyBank, Landmark, CreditCard, Banknote, HandCoins, LineChart, TrendingUp, Bitcoin, Building2, Home, Car, Bike,
  TrainFront, Fuel, Plane, Palmtree, ShoppingCart, ShoppingBag, Utensils, Coffee, Gift, Heart, Stethoscope, Dumbbell, GraduationCap,
  BookOpen, Baby, Dog, Shirt, Smartphone, Zap, Wrench, Receipt, Briefcase, Film, Music, Umbrella, Shield, Target, Sparkles,
};

export const iconByName = (name: string | undefined, fallback: LucideIcon = Wallet): LucideIcon =>
  (name && MONEY_ICONS[name]) || fallback;

const toHex = (rgb: string): string => {
  if (rgb.startsWith('#')) return rgb.toLowerCase();
  const m = /(\d+)\D+(\d+)\D+(\d+)/.exec(rgb);
  if (!m) return rgb;
  return `#${[m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, '0')).join('')}`;
};

/** Colour swatches for user records, resolved from the chart tokens (records store `#rrggbb`). */
export const useSwatches = (): string[] => {
  const theme = useChartTheme();
  return useMemo(() => theme.series.map(toHex), [theme.series]);
};

export interface ColorPickerProps {
  readonly value: string | undefined;
  readonly onChange: (hex: string | undefined) => void;
  readonly label?: string;
}

/** Radio-style row of colour swatches (arrow keys not needed: each swatch is a 44px button with a name). */
export const ColorPicker = ({ value, onChange, label = 'Colour' }: ColorPickerProps) => {
  const swatches = useSwatches();
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1">
      <button
        type="button"
        role="radio"
        aria-checked={!value}
        aria-label="No colour"
        onClick={() => onChange(undefined)}
        className={cn('flex size-11 items-center justify-center rounded-md border border-border text-xs text-text-muted hover:bg-surface-sunken', !value && 'ring-2 ring-primary')}
      >
        Auto
      </button>
      {swatches.map((hex, i) => (
        <button
          key={`${hex}-${i}`}
          type="button"
          role="radio"
          aria-checked={value?.toLowerCase() === hex}
          aria-label={`Colour ${i + 1}`}
          onClick={() => onChange(hex)}
          className={cn('flex size-11 items-center justify-center rounded-md hover:bg-surface-sunken', value?.toLowerCase() === hex && 'ring-2 ring-primary')}
        >
          <span className="size-6 rounded-full" style={{ background: hex }} aria-hidden />
        </button>
      ))}
    </div>
  );
};

export interface IconPickerProps {
  readonly value: string | undefined;
  readonly onChange: (name: string | undefined) => void;
  readonly label?: string;
}

/** Grid of curated icons; the icon name is the accessible label. */
export const IconPicker = ({ value, onChange, label = 'Icon' }: IconPickerProps) => (
  <div role="radiogroup" aria-label={label} className="grid grid-cols-[repeat(auto-fill,minmax(2.75rem,1fr))] gap-1">
    {Object.entries(MONEY_ICONS).map(([name, Icon]) => (
      <button
        key={name}
        type="button"
        role="radio"
        aria-checked={value === name}
        aria-label={name.replace(/([a-z])([A-Z])/g, '$1 $2')}
        onClick={() => onChange(value === name ? undefined : name)}
        className={cn(
          'flex size-11 items-center justify-center rounded-md text-text-muted hover:bg-surface-sunken hover:text-text',
          value === name && 'bg-primary-soft text-primary ring-2 ring-primary',
        )}
      >
        <Icon className="size-5" aria-hidden />
      </button>
    ))}
  </div>
);

export interface RecordIconProps {
  readonly icon?: string | undefined;
  readonly color?: string | undefined;
  readonly fallback?: LucideIcon;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly className?: string;
}

/** Round tile with a record's icon tinted by its colour (falls back to primary). Decorative. */
export const RecordIcon = ({ icon, color, fallback, size = 'md', className }: RecordIconProps) => {
  const Icon = iconByName(icon, fallback);
  return (
    <span
      aria-hidden
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full text-primary',
        size === 'sm' && 'size-7 [&_svg]:size-3.5',
        size === 'md' && 'size-9 [&_svg]:size-4',
        size === 'lg' && 'size-12 [&_svg]:size-6',
        className,
      )}
      style={color ? { color } : undefined}
    >
      <span className="absolute inset-0 bg-current opacity-15" />
      <Icon className="relative" />
    </span>
  );
};
